import { Component, GameObject, HelperFunctions, IVector2, System } from "../../tsthree";
import { ENGINE_DEBUG_MODE, GAME_DEBUG_MODE } from "../../engine/Constants/Constants";
import { FTUTutorialComponent } from "../Components/FTUTutorialComponent";
import {
    BLEND_MODES,
    Container,
    DisplayObject,
    Graphics,
    NineSlicePlane, Rectangle,
    Sprite,
    Text,
    TextStyle,
    Texture
} from "pixi.js";
import { tsthreeConfig } from "../../config/tsthreeConfig";
import { Scopey } from "../Prefabs/Scopey";
import TaggedText from "pixi-tagged-text";
import { GAME_FONT, GAME_FONT_BOLD, GAME_FONT_ITALIC } from "../Constants/Constants";
import { buttonify } from "../../engine/HelperFunctions/buttonify";
import { TutorialStageComponent } from "../Components/TutorialStageComponent";
import { getScreenSpacePosition } from "../../engine/HelperFunctions/getScreenSpacePosition";
import { bringToFront } from "../../engine/HelperFunctions/bringToFront";
import { PlayerDataSingleton } from "../Logic/PlayerDataSingleton";
import { Windows95 } from "../States/Windows95";

const TUTORIAL_STAGES: Array<{
    id?: string; // some arbitrary reference
    text: string;
    showNextButton: boolean;
    targets?: Array<string>;
    spotlightShape?: "circle" | "rectangle";
    spotlightAnchor?: IVector2;
    textLocation?: "top-left" | "top-right" | "top";
    spotlightOpacity?: number;
}> = [
    {
        // setup
        text: "",
        showNextButton: false,
        textLocation: "top-left",
        targets: [],
    },
    {
        text: "Hi!\n\nI see you're trying to get started in DoctOS!\n\nLet’s step through the basics and you’ll be solving cases in no time!",
        showNextButton: true,
        textLocation: "top-left",
        targets: [],
    },
    {
        text: "First of all, let’s open the Mail program, by double-clicking the icon.\n\nThe Mail program is your one-stop solution for keeping on-top of your cases, past and present! You can order exams, treatments, and ultimately diagnose patients, all from your inbox!",
        showNextButton: false,
        spotlightShape: "circle",
        spotlightAnchor: { x: 0.1, y: 0 },
        targets: [
            "MailDesktopIconTutorial"
        ]
    },
    {
        text: "Oh! Looks like you already have an email!\n\nClick the email in the section above to open it",
        showNextButton: false,
        spotlightShape: "rectangle",
        spotlightOpacity: 1,
        targets: [
            "MailLineStage"
        ]
    },
    {
        text: "Awwh, what a nice email!",
        showNextButton: true,
        spotlightShape: "rectangle",
        textLocation: "top-right",
        spotlightOpacity: 1,
        targets: [
            "CaseEmailStage"
        ]
    },
    {
        text: "Well, it isn’t actually needed right now, and we don't want a cluttered inbox!\n\nYou can delete emails by selecting them, and then clicking 'Edit' and 'Delete email' at the top.",
        showNextButton: false,
        spotlightShape: "rectangle",
        textLocation: "top-left",
        spotlightOpacity: 1,
        targets: [
            "DeleteEmailStage"
        ]
    },
    {
        text: "There, much better! A clear inbox is a clear mind, after all.\n\nNow, let’s start solving cases!\n\nYou can request new cases by accessing the Start menu and clicking the button at the top.",
        showNextButton: false,
        spotlightShape: "rectangle",
        textLocation: "top-left",
        spotlightOpacity: 0,
        targets: [
            "TaskbarTutorialStage"
        ]
    },
    {
        text: "From here, you can choose which cases you would like to take.\n\nYou’ll want to keep an eye on the difficulty and number of symptoms in each case, as these will be the best indicators of how difficult the case will be.",
        showNextButton: true,
        spotlightShape: "rectangle",
        spotlightOpacity: 0,
        textLocation: "top-right",
        targets: [
            "SelectACaseStage"
        ]
    },
    {
        text: "The department also determines what type of case you'll receive.\n\nFor example, cases from the orthopaedics department will likely not present with any psychological symptoms. Use this to your advantage!\n\nNow, choose a case!",
        showNextButton: false,
        spotlightShape: "rectangle",
        spotlightOpacity: 0,
        targets: [
            "SelectACaseStage2"
        ]
    },
    {
        text: "Now that you’ve got a case, it's time to get diagnosing!\n\nFrom the case email, you'll find all the relevant information linked to the World Wide Web for ease of use.",
        showNextButton: false,
        spotlightShape: "rectangle",
        spotlightOpacity: 1,
        targets: [
            "MailLineStage"
        ]
    },
    {
        id: "BASIC_EXAM_TUTORIAL",
        text: "You should always first schedule a basic exam.\n\nA Physical Examination or even just talking to the patient via an Interview exam is the best way to reveal lots of symptoms quickly.",
        showNextButton: true,
        spotlightShape: "rectangle",
        textLocation: "top-left",
        spotlightOpacity: 1,
        targets: [
            "CaseEmailStage"
        ]
    },
    {
        id: "SCHEDULE_EXAM_TUTORIAL",
        text: "Whenever you schedule an exam or treatment, you’ll receive a confirmation email that will let you know when it should be done by.",
        showNextButton: true,
        spotlightShape: "rectangle",
        spotlightOpacity: 0,
        targets: []
    },
    {
        id: "OPEN_INTERNET_TRAVELER",
        text: "Before we can schedule exams though, let’s get you acquainted with Internet Traveller.\n\nYou can click any of the Visible Symptoms in the case email to quickly view them on the Internet.",
        showNextButton: false,
        spotlightShape: "rectangle",
        textLocation: "top-left",
        spotlightOpacity: 1,
        targets: ["CaseEmailStage"]
    },
    {
        text: "Using Drugle, you can easily lookup information about symptoms and diseases, which will be invaluable in solving cases!",
        showNextButton: true,
        spotlightShape: "rectangle",
        textLocation: "top-right",
        spotlightOpacity: 1,
        targets: ["WebpageStage"]
    },
    {
        text: "The information on what diagnoses present with what symptoms is particularly helpful. The text marked in <bold>bold</bold> are the diagnoses that share the most common examination. Most- if not all- diagnoses have a 'smoking gun' symptom that is revealed by a specific examination. You should try these first before other exams!",
        showNextButton: true,
        spotlightShape: "rectangle",
        textLocation: "top-right",
        spotlightOpacity: 1,
        targets: ["WebpageStage"]
    },

//     You can access the Drugle home
//     page by clicking the “Home” button
// at the top of the screen.
    {
        text: "If you ever get lost in a sea of diagnostic webpages, you can always return to the Drugle homepage by clicking the Home button.",
        showNextButton: false,
        spotlightShape: "rectangle",
        textLocation: "top-right",
        spotlightAnchor: { x: -0.5, y: -0.42 },
        spotlightOpacity: 1,
        targets: ["HomeButtonStage"]
    },
    {
        text: "From the homepage, you can search for specific information on diseases, exams, treatments, and symptoms!",
        showNextButton: true,
        spotlightShape: "rectangle",
        textLocation: "top-right",
        spotlightOpacity: 1,
        targets: ["WebpageStage"]
    },
    {
        text: "The taskbar at the bottom of the screen is also for navigating between running programs.\n\nLet's click on the one for Mail to go back to your inbox.",
        showNextButton: false,
        spotlightShape: "rectangle",
        textLocation: "top-left",
        spotlightOpacity: 1,
        targets: ["MailWindow_TaskbarTutorial"]
    },
    {
        text: "Exams and treatments both will reveal more symptoms, but be <bold>very careful with treatments!</bold> Treating non-existent symptoms will <bold>negatively</bold> affect your Doctor Score, and jeopardize the patients wellbeing!",
        showNextButton: true,
        spotlightShape: "rectangle",
        textLocation: "top-right",
        spotlightOpacity: 1,
        targets: ["MailWindow_TaskbarTutorial"]
    },
    {
        text: "To get the most doctor score, you will want to:\n" +
            " - Discover all symptoms\n" +
            " - Treat all symptoms\n" +
            " - Diagnose the malady!\n\n" +
            "Don’t forget: diagnosing is the <bold>LAST</bold> thing to do!",
        showNextButton: true,
        spotlightShape: "rectangle",
        textLocation: "top-right",
        spotlightOpacity: 0,
        targets: []
    },
    {
        text: "It's also worth mentioning: <bold>you don't need to wait for a treatment to finish before diagnosing;</bold> if you’re sure in your diagosis, the treatment will work!",
        showNextButton: true,
        spotlightShape: "rectangle",
        textLocation: "top-right",
        spotlightOpacity: 0,
        targets: []
    },
    {
        text: "And that’s pretty much all there is!\n\n" +
            "When in doubt, you can access the Help program for more information.\n\n" +
            "Now go forth and diagnose!",
        showNextButton: true,
        spotlightShape: "rectangle",
        textLocation: "top-left",
        spotlightOpacity: 0,
        targets: []
    },
];

export class FTUTutorialSystem extends System {
    public static onAwake(_component: FTUTutorialComponent): void {
        _component.parent.zIndex = Number.MAX_SAFE_INTEGER - 10;

        const overlay = _component.overlay = new Graphics();
        overlay.beginFill(0x010101, 0.44);
        overlay.drawRect(0, 0, 100, 100);
        overlay.endFill();
        overlay.width = tsthreeConfig.width;
        overlay.height = tsthreeConfig.height;
        overlay.interactive = overlay.interactiveChildren = true;
        _component.parent.addChild(overlay);

        const spotlight = _component.spotlight = new Graphics();
        spotlight.beginFill(0xfafafa, 1);
        spotlight.drawCircle(0, 0, 50);
        spotlight.endFill();
        // spotlight.scale.set(
        //     1 / overlay.scale.x,
        //     1 / overlay.scale.y,
        // );
        spotlight.visible = false;
        _component.parent.addChild(spotlight);

        const rectSpotlight = _component.rectSpotlight = new Graphics();
        rectSpotlight.beginFill(0xfafafa, 1);
        rectSpotlight.drawRect(0, 0, 10, 10);
        rectSpotlight.endFill();
        // rectSpotlight.scale.set(
        //     1 / overlay.scale.x,
        //     1 / overlay.scale.y,
        // );
        rectSpotlight.visible = false;
        _component.parent.addChild(rectSpotlight);
        // _component.parent.mask = overlayMask;

        const scopey = _component.scopey = new Scopey();
        scopey.position.set(tsthreeConfig.width, tsthreeConfig.height);
        // scopey.scale.set(0.5);
        _component.parent.addChild(scopey);

        const popupContainer = _component.popup = new Container();
        scopey.addChild(popupContainer);
        const popup = _component.popupNineslice = new NineSlicePlane(
            ENGINE.getPIXIResource("popup_nineslice") as Texture
        );
        popup.width = 200;
        popup.height = 125;
        // popup.scale.set(2);
        popupContainer.addChild(popup);
        FTUTutorialSystem.setPositionBasedOnSection(
            "top-left",
            popupContainer,
            scopey
        );
        _component.cachedPos = {
            x: popupContainer.x,
            y: popupContainer.y,
        };

        const arrow = _component.arrow = new Sprite(ENGINE.getPIXIResource("popup_arrow") as Texture);
        popup.addChild(arrow);

        const text = _component.text = new TaggedText("", {
            "default": {
                fontFamily: GAME_FONT,
                align: "left",
                wordWrapWidth: ((popup.width * popup.scale.x) - 24),
                wordWrap: true,
                fontSize: 11
            },
            "bold": {
                fontFamily: GAME_FONT_BOLD,
            }
        });
        // text.scale.set(2);
        // text.position.set(12, 8);
        // text.scale.set(1);
        popupContainer.addChild(text);

        const nextButtonContainer = _component.nextButtonContainer = new Container();

        // HACK
        // @ts-ignore
        nextButtonContainer["__HANDLE_NEXT_FUNC"] = null;
        buttonify(nextButtonContainer, {
            onFire: () => {
                if(_component.stage === 0) return;
                console.log("Next");
                // @ts-ignore
                nextButtonContainer["__HANDLE_NEXT_FUNC"]?.();
            }
        });
        // HACK

        const nextButtonText = _component.nextButtonText = new TaggedText("Next", {
            default: {
                fontFamily: GAME_FONT,
                fill: 0x0000ff,
                textDecoration: "underline",
                fontSize: 11
            }
        });
        // nextButtonText.scale.set(1.6);
        nextButtonText.anchor.set(0.5, 1);
        nextButtonContainer.addChild(nextButtonText);
        nextButtonContainer.position.set(
            (popup.width * popup.scale.x) - 40,
            0
        );
        popupContainer.addChild(nextButtonContainer);

        if(GAME_DEBUG_MODE) {
            const skipButtonContainer = _component.skipButtonContainer = new Container();
            skipButtonContainer.alpha = 0.5;
            const skipButtonText = _component.skipButtonText = new TaggedText("Skip", {
                default: {
                    fontFamily: GAME_FONT,
                    fill: 0x0000ff,
                    textDecoration: "underline",
                    fontSize: 11
                }
            });
            // skipButtonText.scale.set(1.6);
            skipButtonText.anchor.set(0.5, 1);
            skipButtonContainer.addChild(skipButtonText);
            const skipButtonGraphic = new Graphics();
            skipButtonGraphic.lineStyle(1, 0x040404);
            skipButtonContainer.position.set(
                0 + 40,
                0
            );
            popupContainer.addChild(skipButtonContainer);
            buttonify(skipButtonContainer, {
                onFire: () => {
                    if(GAME_DEBUG_MODE) console.log("Skipped tutorial");
                    _component.parent.destroy();
                }
            });
        }

        FTUTutorialSystem.updateElements(
            "",
            _component,
            false,
            undefined
        );

        _component.parent.visible = false;
    }

    private static updateElements(
        _text: string,
        _component: FTUTutorialComponent,
        _showNextButton: boolean,
        _handleNext: undefined | boolean | (() => void),
    ): void {
        _component.nextButtonContainer.visible = _showNextButton;
        _component.text.text = _text;
        _component.text.update();
        _component.popupNineslice.height = Math.max(
            50,
            ((_component.text.textContainer.height)) + (_component.nextButtonContainer.height + 17)
        );
        if(!_component.nextButtonContainer.visible) {
            _component.text.position.set(
                12, (
                    (
                        _component.popupNineslice.height * _component.popupNineslice.scale.y
                    ) - (
                        _component.text.textContainer.height * _component.text.scale.y
                )) * 0.5
            );
        } else {
            _component.text.position.set(
                12, 8
            );
            _component.nextButtonContainer.position.set(
                (_component.popupNineslice.width * _component.popupNineslice.scale.x) - 40,
                (_component.popupNineslice.height * _component.popupNineslice.scale.y) - 20
            );
            _component.skipButtonContainer?.position.set(
                _component.nextButtonContainer.x - 50,
                (_component.popupNineslice.height * _component.popupNineslice.scale.y) - 20
            );
        }
        _component.arrow.position.set(_component.popupNineslice.width - 18, _component.popupNineslice.height - 1);
        FTUTutorialSystem.setPositionBasedOnSection(
            _component.currentPositionSetting,
            _component.popup,
            _component.scopey
        );
        _component.cachedPos = {
            x: _component.popup.x,
            y: _component.popup.y,
        };

        if(_handleNext) {
            // HACK
            // @ts-ignore
            _component.nextButtonContainer["__HANDLE_NEXT_FUNC"] = _handleNext;
            // HACK
        }
    }

    private static setPositionBasedOnSection(
        _section: "top" | "top-left" | "top-right",
        _target: NineSlicePlane | Sprite | GameObject | Container,
        _scopey: Scopey
    ): void {
        switch(_section) {
            case "top":
                break;
            case "top-left":
                _target.position.set(
                    -_target.width - _scopey.mainSprite.width + 20,
                    Math.max(
                        -_target.height - _scopey.mainSprite.height + 20,
                        -(tsthreeConfig.height - 20),
                    )
                );
                break;
            case "top-right":
                _target.position.set(
                    -_target.width + -30,
                    Math.max(
                        -_target.height + -_scopey.mainSprite.height + -20,
                        -(tsthreeConfig.height - 20),
                    )
                );
                break;
        }
    }

    private static handleNext(_component: FTUTutorialComponent): void {
        FTUTutorialSystem.setStage(_component.stage + 1, _component);
    }

    private static setStage(stage: number, _component: FTUTutorialComponent, _force?: boolean): void {
        if(
            _force ||
            (TUTORIAL_STAGES[stage] && _component.text.text !== TUTORIAL_STAGES[stage].text)
        ) {
            if(stage > 1 && _component.skipButtonContainer) {
                _component.skipButtonContainer.visible = false;
            }
            _component.arbitraryStageId = TUTORIAL_STAGES[stage].id || null;
            _component.text.text = TUTORIAL_STAGES[stage].text;
            _component.currentPositionSetting =
                TUTORIAL_STAGES[stage].textLocation || _component.currentPositionSetting;
            FTUTutorialSystem.updateElements(
                TUTORIAL_STAGES[stage].text,
                _component,
                TUTORIAL_STAGES[stage].showNextButton,
                () => FTUTutorialSystem.handleNext(_component)
            );

            _component.currentTargets.forEach((e) => {
                if(GAME_DEBUG_MODE) console.log("Reparenting %o with %o", e.obj, e.parent);
                e.obj.getComponent(TutorialStageComponent).onActivate = null;
                e.parent.addChild(e.obj);
                e.obj.scale.set(e.scale.x, e.scale.y);
                e.obj.position.set(e.pos.x, e.pos.y);
            });
            _component.currentTargets = [];

            if(!TUTORIAL_STAGES[stage+1]) {
                _component.nextButtonText.text = "Done";
            }

            // find element
            if(TUTORIAL_STAGES[stage].targets && TUTORIAL_STAGES[stage].targets.length) {
                const targets: TutorialStageComponent[] = [];
                TUTORIAL_STAGES[stage].targets.forEach((e) => {
                    targets.push(
                        ...TutorialStageComponent.findInstances<TutorialStageComponent>((c) => {
                            return c.tutorialStageId === e;
                        })
                    );
                });
                if(GAME_DEBUG_MODE)
                    console.log(targets);

                const cache: Array<{
                    obj: GameObject,
                    pos: IVector2,
                    scale: IVector2,
                    parent: DisplayObject
                }> = _component.currentTargets = [];
                targets.forEach((e, i) => {
                    const worldPos = getScreenSpacePosition(e.parent);
                    e.onActivate = () => {
                        e.onActivate = null;
                        FTUTutorialSystem.setStage(_component.stage + 1, _component);
                    };
                    cache.push({
                        obj: e.parent,
                        parent: e.parent.parent,
                        scale: {
                            x: e.parent.scale.x,
                            y: e.parent.scale.y,
                        },
                        pos: {
                            x: e.parent.position.x,
                            y: e.parent.position.y,
                        }
                    });

                    e.parent.parent.removeChild(e.parent);
                    _component.parent.addChild(
                        e.parent
                    );
                    // e.parent.scale.set(
                    //     e.parent.scale.x / _component.overlay.scale.x,
                    //     e.parent.scale.y / _component.overlay.scale.y,
                    // );
                    e.parent.position.set(
                        worldPos.x,
                        worldPos.y
                    );
                    e.parent.updateTransform();
                    if(i === 0) {
                        _component.rectSpotlight.visible =
                            TUTORIAL_STAGES[stage].spotlightShape === "rectangle";
                        _component.spotlight.visible =
                            TUTORIAL_STAGES[stage].spotlightShape === "circle";


                        _component.spotlight.alpha = _component.rectSpotlight.alpha =
                            typeof TUTORIAL_STAGES[stage].spotlightOpacity !== "undefined" ?
                                TUTORIAL_STAGES[stage].spotlightOpacity
                                : 0.22;

                        if(GAME_DEBUG_MODE) {
                            console.log("_component.spotlight.scale = %o", _component.spotlight.scale);
                            console.log("e.parent.position = %o", e.parent.position);
                            console.log("e.parent.scale = %o", e.parent.scale);
                        }
                        _component.spotlight.position.set(
                            e.parent.position.x + (e.parent.width * e.parent.scale.x * 0.5) + (_component.spotlight.width * (TUTORIAL_STAGES[stage].spotlightAnchor?.x || 0)),
                            e.parent.position.y + (e.parent.height * e.parent.scale.y * 0.5) + (_component.spotlight.height * (TUTORIAL_STAGES[stage].spotlightAnchor?.y || 0)),
                        );

                        _component.rectSpotlight.width = e.parent.width * e.parent.scale.x;
                        _component.rectSpotlight.height = e.parent.height * e.parent.scale.y;
                        _component.rectSpotlight.position.set(
                            e.parent.position.x + (_component.rectSpotlight.width * (TUTORIAL_STAGES[stage].spotlightAnchor?.x || 0)),
                            e.parent.position.y + (_component.rectSpotlight.height * (TUTORIAL_STAGES[stage].spotlightAnchor?.y || 0)),
                        );
                    }
                });
                // get world pos of each target
                // cache their parent + local pos
                // add as child of overlay element and set pos as world pos
                // add temporary input hook
            } else {
                _component.spotlight.visible = false;
                _component.rectSpotlight.visible = false;
            }
            bringToFront(_component.scopey);
            bringToFront(_component.parent);
        }
        else if(!TUTORIAL_STAGES[stage]) {
            // closing tutorial
            // update playerdatasingleton here
            _component.currentTargets.forEach((e) => {
                e.parent.addChild(e.obj);
                e.obj.scale.set(e.scale.x, e.scale.y);
                e.obj.position.set(e.pos.x, e.pos.y);
            });
            _component.currentTargets = [];
            TutorialStageComponent.instances.forEach((e) => {
                e.parent.removeComponent(e);
            });
            _component.parent.destroy();
        }
        if(GAME_DEBUG_MODE)
            console.log("Updating tutorial from stage %i to stage %i", _component.stage, stage);
        _component.stage = stage;
    }

    public static onStep(_dt: number, _component: FTUTutorialComponent): void {
        if(_component.stage < 0)
            return;

        _component.timer += _dt;

        if(_component.stage === 0) {
            // setup time
            if(TUTORIAL_STAGES[1] && _component.text.text !== TUTORIAL_STAGES[1].text) {
                FTUTutorialSystem.setStage(1, _component);
                _component.stage = 0;
            }
            _component.parent.visible = true;
            // _component.text.visible = false;

            _component.overlay.alpha =
                Math.min(1, _component.timer * 0.02);

            _component.scopey.alpha =
                Math.max(0, Math.min(2, _component.timer * 0.02) - 1);

            _component.popup.alpha =
                Math.max(0, Math.min(3, _component.timer * 0.02) - 2);

            if(_component.popup.alpha >= 1) {
                _component.popup.alpha = 1;
                FTUTutorialSystem.setStage(1, _component, true);
            }
        }

        _component.popup.position.set(
            _component.cachedPos.x,
            _component.cachedPos.y + Math.round(Math.sin(_component.timer * 0.02) * 5),
        );
    }

    public static onDestroy(_component: FTUTutorialComponent): void {
        PlayerDataSingleton.setSeenFTUTutorial(true);
        (ENGINE.getActiveState() as Windows95).playMusic();
    }

    public static onEnable(_component: FTUTutorialComponent): void {
    }

    public static onDisable(_component: FTUTutorialComponent): void {
    }

    public static destroy(_component: FTUTutorialComponent): void {
        this.onDestroy(_component);
    }
}